// src/admin/AdminMerchants.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, updateDoc,
  setDoc, serverTimestamp, query, where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Users, Search, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, Edit2, Save,
  X, Building2, CreditCard, MapPin, Phone,
  Instagram, ExternalLink, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Pending Request Card ───────────────────────────────────────────
function RequestCard({ req, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const date = req.submittedAt?.toDate?.()?.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  }) || '—';

  const handleApprove = async () => {
    setLoading(true);
    try { await onApprove(req); toast.success(`${req.shopName} approved! ✅`); }
    catch { toast.error('Approval failed'); }
    finally { setLoading(false); }
  };

  const handleReject = async () => {
    if (!window.confirm(`Reject ${req.shopName}'s application?`)) return;
    setLoading(true);
    try { await onReject(req); toast.success('Request rejected'); }
    catch { toast.error('Rejection failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-amber-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="w-10 h-10 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 font-bold text-base shrink-0">
          {req.shopName?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-body font-bold text-gray-900 text-sm">{req.shopName}</p>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-body font-semibold">
              ⏳ Pending
            </span>
          </div>
          <p className="text-gray-400 font-body text-xs">{req.userEmail} · {req.category} · {date}</p>
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </div>

      {expanded && (
        <div className="border-t border-amber-100 p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider mb-2">Applicant</p>
              <p className="font-body font-medium text-gray-900 text-sm">{req.userName}</p>
              <p className="font-body text-gray-500 text-xs">{req.userEmail}</p>
              <p className="font-body text-gray-500 text-xs flex items-center gap-1 mt-1">
                <Phone size={11} /> {req.phone}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider mb-2">Shop Info</p>
              <p className="font-body font-medium text-gray-900 text-sm">{req.shopName}</p>
              <p className="font-body text-gray-500 text-xs">Category: {req.category}</p>
              {req.socialLink && (
                <a href={req.socialLink} target="_blank" rel="noreferrer"
                  className="font-body text-rose-500 text-xs flex items-center gap-1 mt-1 hover:underline">
                  <ExternalLink size={11} /> Portfolio/Social
                </a>
              )}
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-3">
            <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider mb-1">What they sell</p>
            <p className="font-body text-gray-700 text-sm leading-relaxed">{req.description}</p>
          </div>

          {req.experience && (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider mb-1">Experience</p>
              <p className="font-body text-gray-700 text-sm">{req.experience}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={handleApprove} disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-2xl font-body font-semibold text-sm transition-all disabled:opacity-60">
              {loading
                ? <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                : <CheckCircle size={16} />
              }
              Approve & Grant Access
            </button>
            <button onClick={handleReject} disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 py-3 rounded-2xl font-body font-semibold text-sm transition-all disabled:opacity-60">
              <XCircle size={16} /> Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Active Merchant Card ───────────────────────────────────────────
function MerchantCard({ user, profile, onUpdate }) {
  const [expanded,   setExpanded]   = useState(false);
  const [editing,    setEditing]    = useState(false);
  const [commission, setCommission] = useState(profile?.commissionRate || 10);
  const [razorpayId, setRazorpayId] = useState(profile?.razorpayLinkedAccountId || '');
  const [saving,     setSaving]     = useState(false);
  const [toggling,   setToggling]   = useState(false);

  const isActive = user.role === 'merchant' || user.role === 'admin';

  const toggleAccess = async () => {
    setToggling(true);
    try {
      const newRole = isActive ? 'user' : 'merchant';
      await updateDoc(doc(db, 'users', user.id), { role: newRole });
      onUpdate(user.id, { role: newRole });
      toast.success(isActive ? 'Merchant access revoked' : 'Merchant access granted ✅');
    } catch { toast.error('Failed to update access'); }
    finally { setToggling(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'merchants', user.id), {
        commissionRate:          Number(commission),
        razorpayLinkedAccountId: razorpayId.trim(),
        updatedByAdmin:          true,
      }, { merge: true });
      onUpdate(user.id, { commissionRate: Number(commission), razorpayLinkedAccountId: razorpayId.trim() });
      toast.success('Settings saved ✅');
      setEditing(false);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="w-11 h-11 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-base shrink-0">
          {user.displayName?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-body font-bold text-gray-900 text-sm">{user.displayName}</p>
            {profile?.shopName && (
              <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-body font-semibold">
                {profile.shopName}
              </span>
            )}
          </div>
          <p className="text-gray-400 font-body text-xs">{user.email}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
            isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {isActive ? '✓ Active' : 'Revoked'}
          </span>
          {profile?.commissionRate && (
            <span className="hidden sm:inline text-xs bg-rose-50 text-rose-600 px-2 py-1 rounded-full font-body font-semibold">
              {profile.commissionRate}%
            </span>
          )}
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-5 space-y-4">
          {/* Profile summary */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Building2 size={13} className="text-purple-500" />
                <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider">Shop</p>
              </div>
              <p className="font-body font-medium text-gray-900 text-sm">{profile?.shopName || '—'}</p>
              {profile?.gstNumber && <p className="font-body text-gray-400 text-xs mt-1">GST: {profile.gstNumber}</p>}
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <MapPin size={13} className="text-blue-500" />
                <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider">Pickup</p>
              </div>
              {profile?.pickupCity ? (
                <p className="font-body text-gray-700 text-xs">{profile.pickupCity}, {profile.pickupState} — {profile.pickupPincode}</p>
              ) : <p className="font-body text-gray-400 text-xs">Not set</p>}
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <CreditCard size={13} className="text-green-500" />
                <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider">Bank</p>
              </div>
              {profile?.bankName ? (
                <>
                  <p className="font-body text-gray-700 text-xs">{profile.bankName}</p>
                  <p className="font-body text-gray-400 text-xs">IFSC: {profile.ifscCode}</p>
                </>
              ) : <p className="font-body text-gray-400 text-xs">Not set</p>}
            </div>
          </div>

          {/* Admin controls */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-body font-semibold text-gray-700 text-sm">Admin Controls</p>
              <button onClick={() => setEditing(!editing)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-rose-500 border border-gray-200 hover:border-rose-300 px-3 py-1.5 rounded-xl transition-all">
                {editing ? <><X size={12} /> Cancel</> : <><Edit2 size={12} /> Edit</>}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Commission Rate (%)</label>
                <input type="number" value={commission} onChange={(e) => setCommission(e.target.value)}
                  disabled={!editing} min="0" max="50"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-sm disabled:opacity-60 disabled:bg-gray-50" />
                <p className="text-xs text-gray-400 font-body mt-1">Merchant earns {100 - commission}%</p>
              </div>
              <div>
                <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Razorpay Linked Account ID</label>
                <input type="text" value={razorpayId} onChange={(e) => setRazorpayId(e.target.value)}
                  disabled={!editing} placeholder="route_XXXXXXXXXX"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-sm disabled:opacity-60 disabled:bg-gray-50" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {editing && (
                <button onClick={saveSettings} disabled={saving}
                  className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-5 py-2.5 rounded-xl font-body font-semibold text-sm transition-all disabled:opacity-60">
                  {saving ? <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" /> : <Save size={15} />}
                  Save
                </button>
              )}
              <button onClick={toggleAccess} disabled={toggling}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-body font-semibold text-sm transition-all disabled:opacity-60 ${
                  isActive
                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                    : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                }`}>
                {isActive ? <><XCircle size={15} /> Revoke Access</> : <><CheckCircle size={15} /> Restore Access</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function AdminMerchants() {
  const [requests,   setRequests]   = useState([]);
  const [merchants,  setMerchants]  = useState([]);
  const [profiles,   setProfiles]   = useState({});
  const [allUsers,   setAllUsers]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [activeTab,  setActiveTab]  = useState('requests');

  const fetchData = async () => {
    try {
      const [reqSnap, usersSnap, merchantsSnap] = await Promise.all([
        getDocs(query(collection(db, 'merchantRequests'), where('status', '==', 'pending'))),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'merchants')),
      ]);

      setRequests(reqSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllUsers(users);
      setMerchants(users.filter((u) => u.role === 'merchant' || u.role === 'admin'));

      const pm = {};
      merchantsSnap.docs.forEach((d) => { pm[d.id] = d.data(); });
      setProfiles(pm);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Approve a merchant request
  const handleApprove = async (req) => {
    // 1. Update request status
    await updateDoc(doc(db, 'merchantRequests', req.id), {
      status:     'approved',
      reviewedAt: serverTimestamp(),
    });
    // 2. Set user role to merchant
    await updateDoc(doc(db, 'users', req.userId), { role: 'merchant' });
    // 3. Create merchant profile stub
    await setDoc(doc(db, 'merchants', req.userId), {
      uid:           req.userId,
      email:         req.userEmail,
      displayName:   req.userName,
      shopName:      req.shopName,
      category:      req.category,
      commissionRate: 10,
      status:        'active',
      approvedAt:    serverTimestamp(),
    }, { merge: true });

    // Remove from pending list
    setRequests((prev) => prev.filter((r) => r.id !== req.id));
    // Add to merchants list
    const newMerchant = allUsers.find((u) => u.id === req.userId);
    if (newMerchant) {
      setMerchants((prev) => [...prev, { ...newMerchant, role: 'merchant' }]);
      setProfiles((prev) => ({ ...prev, [req.userId]: { shopName: req.shopName, commissionRate: 10 } }));
    }
  };

  // Reject a merchant request
  const handleReject = async (req) => {
    await updateDoc(doc(db, 'merchantRequests', req.id), {
      status:     'rejected',
      reviewedAt: serverTimestamp(),
    });
    setRequests((prev) => prev.filter((r) => r.id !== req.id));
  };

  const handleMerchantUpdate = (userId, updates) => {
    setMerchants((prev) => prev.map((u) => u.id === userId ? { ...u, ...updates } : u));
    if (updates.commissionRate !== undefined || updates.razorpayLinkedAccountId !== undefined) {
      setProfiles((prev) => ({ ...prev, [userId]: { ...prev[userId], ...updates } }));
    }
  };

  const filteredMerchants = merchants.filter((u) =>
    !search ||
    u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    profiles[u.id]?.shopName?.toLowerCase().includes(search.toLowerCase())
  );

  const TABS = [
    { id: 'requests',  label: 'Pending Requests', count: requests.length  },
    { id: 'merchants', label: 'Active Merchants',  count: merchants.length },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display font-bold text-xl text-gray-900">Merchant Management</h2>
        <p className="text-gray-400 font-body text-sm">
          {requests.length} pending · {merchants.length} active merchants
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-2xl p-1 border border-gray-100 shadow-sm w-fit">
        {TABS.map(({ id, label, count }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-body font-semibold transition-all ${
              activeTab === id ? 'bg-rose-500 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {label}
            {count > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                activeTab === id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pending Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <Clock size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-body font-medium">No pending requests</p>
              <p className="text-gray-300 font-body text-xs mt-1">New applications will appear here</p>
            </div>
          ) : (
            requests.map((req) => (
              <RequestCard key={req.id} req={req} onApprove={handleApprove} onReject={handleReject} />
            ))
          )}
        </div>
      )}

      {/* Active Merchants Tab */}
      {activeTab === 'merchants' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or shop name..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-sm" />
          </div>

          {filteredMerchants.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <Users size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-body">No merchants found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMerchants.map((user) => (
                <MerchantCard
                  key={user.id}
                  user={user}
                  profile={profiles[user.id] || {}}
                  onUpdate={handleMerchantUpdate}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
